import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Button, Checkbox, Flex, Form, Input, Modal, Table, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import MyText from "../../../../../components/UI/Text/MyText";
import {LocalName} from "../../../../../utils/LocalName";
import {getMicroQualificationById} from "../../../../../store/slices/MicroqualificationSaveSlice";
import FullLoading from "../../../../../components/LoadingComponents/FullLoading";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import classes from "../../Applications/application.module.css";
import GreenButton from "../../../../../components/UI/Buttons/GreenButton";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import RedButton from "../../../../../components/UI/Buttons/RedButton";
import MyButton from "../../../../../components/UI/Buttons/MyButton";
import {useDocumentPreview} from "../MicroQualificationEdit/hooks/useDocumentPreview";
import {useMicroQualificationActions} from "./hooks/useMicroQualificationActions";
import {useCompetenceMatrix} from "../MicroQualificationEdit/hooks/useCompetenceMatrix";
import DocumentPreviewModal from "./components/DocumentPreviewModal";

const {Paragraph} = Typography;

const MicroQualificationView = () => {
    const {id} = useParams();
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {data, isLoading} = useSelector(state => state.microQualification);
    const [openViewDocument, setOpenViewDocument] = useState(false);

    // Хуки
    const {
        isLoadingDocument,
        numPages,
        documentPdf,
        downloadDocument,
        getDocument,
        onDocumentLoadSuccess
    } = useDocumentPreview(data?.id, data?.name?.nameEng);

    const {
        commentForm,
        openViewComment,
        setOpenViewComment,
        sendBackMicroqualification,
        approveMicroqualification,
        commentMicroqualification
    } = useMicroQualificationActions(id);

    const {columns: matrixColumns, dataSource: matrixDataSource} = useCompetenceMatrix(data);

    useEffect(() => {
        dispatch(getMicroQualificationById({id}));
    }, [id, dispatch]);

    // Колонки для таблицы дисциплин
    const disciplinesColumns = [
        {
            title: <MyText center strong>№</MyText>,
            render: (_, __, index) => <MyText center>{index + 1}</MyText>,
            width: '2%',
        },
        {
            title: <MyText center strong><IntlMessage id="amOpDisciplineName"/></MyText>,
            render: (_, record) => <MyText center>{LocalName.getName(record.name)}</MyText>,
            width: '20%',
        },
        {
            title: <MyText center strong><IntlMessage id="amOpDisciplineDesc"/></MyText>,
            render: (_, record) => <Paragraph>{LocalName.getName(record.description)}</Paragraph>,
            width: '25%',
        },
        {
            title: <MyText center strong><IntlMessage id="amOpCreditNum"/></MyText>,
            render: (_, record) => <MyText center>{record.creditNumbers}</MyText>,
            width: '10%',
        },
        {
            title: <MyText center strong><IntlMessage id="amOpAcademHrsNum"/></MyText>,
            render: (_, record) => <MyText center>{record.academicHours}</MyText>,
            width: '10%',
        },
        {
            title: <MyText center strong><IntlMessage id="amOpDisciplineCompetenceCodes"/></MyText>,
            render: (_, record) => (
                <MyText>
                    {record.competences?.map(c => (t("listOfCompetenciesShort") + c.name.number)).join(", ")}
                </MyText>
            ),
            width: '10%',
        }
    ];

    const profStandardsColumns = [
        {
            title: <MyText center strong>№</MyText>,
            render: (_, __, index) => <MyText center>{index + 1}</MyText>,
            width: '2%',
        },
        {
            title: <MyText center strong><IntlMessage id="name2"/></MyText>,
            render: (_, record) => (
                <MyText center>{LocalName.getName(record)}</MyText>
            ),
            width: '20%',
        },
        {
            title: <MyText center strong><IntlMessage id="certificate.code"/></MyText>,
            render: (_, record) => (
                <MyText>{record.code}</MyText>
            ),
            width: '25%',
        },
        {
            title: <MyText center strong><IntlMessage id="date"/></MyText>,
            render: (_, record) => (
                <MyText>{new Date(record.date).toLocaleDateString('kk-KZ')}</MyText>
            ),
            width: '25%',
        }
    ];

    // Колонки для таблицы авторов
    const authorsColumns = [
        {
            title: <MyText center strong>№</MyText>,
            render: (_, __, index) => <MyText center>{index + 1}</MyText>,
            width: '2%',
        },
        {
            title: <MyText center strong><IntlMessage id="fullName"/></MyText>,
            render: (_, record) => <MyText center>{record.fullName}</MyText>,
            width: '20%',
        },
        {
            title: <MyText center strong><IntlMessage id="position"/></MyText>,
            render: (_, record) => (
                <Paragraph>{LocalName.getName(record.position)} {LocalName.getName(record.workPlace)}</Paragraph>
            ),
            width: '25%',
        }
    ];

    const handleOpenDocument = () => {
        getDocument();
        setOpenViewDocument(true);
    };

    if (isLoading) {
        return <FullLoading/>;
    }

    return (
        <Flex style={{width: "100%", position: "relative", minWidth: 800}} gap={20} vertical>
            <BlockLoading isLoading={isLoading || isLoadingDocument}/>

            <MyText strong uppercase size={"larger"}>{LocalName.getName(data?.name)}</MyText>

            <Flex style={{width: "100%", position: "relative"}} vertical gap={"middle"}>
                {/* Кнопки действий */}
                <Flex gap={"large"} align={"center"} justify={"center"}>
                    <GreyButton onClick={handleOpenDocument}>
                        <IntlMessage id={"docPreview"}/>
                    </GreyButton>
                    <MyButton onClick={() => setOpenViewComment(true)}>
                        <IntlMessage id={"applicationsPage.comment"}/>
                    </MyButton>
                    <RedButton onClick={sendBackMicroqualification}>
                        <IntlMessage id={"sendBackToModerator"}/>
                    </RedButton>
                    <GreenButton onClick={approveMicroqualification}>
                        <IntlMessage id={"approve"}/>
                    </GreenButton>
                </Flex>

                {/* Комментарии */}
                {!!data?.comments && data?.comments.length > 0 && (
                    <Flex vertical gap={"small"}>
                        <IntlMessage id={"applicationsPage.comment"}/>
                        {data?.comments.map((c, index) => (
                            <Input.TextArea key={index} rootClassName={classes.formInput} value={c.comment} readOnly/>
                        ))}
                    </Flex>
                )}

                {/* Общая информация */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.generalInfo"}/></MyText>
                    <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"} gap={"small"}>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"educationField"}/></MyText>
                            <Input rootClassName={classes.formInput}
                                   value={LocalName.getName(data?.secondStep?.educationField)} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"trainingDirection"}/></MyText>
                            <Input rootClassName={classes.formInput}
                                   value={LocalName.getName(data?.secondStep?.trainingDirection)} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"groupEP"}/></MyText>
                            <Input rootClassName={classes.formInput}
                                   value={LocalName.getName(data?.secondStep?.groupEP)} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"licenceNumberTo"}/></MyText>
                            <Input rootClassName={classes.formInput} value={data?.firstStep?.licenceNumber} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"creditVolume"}/></MyText>
                            <Input rootClassName={classes.formInput} value={data?.firstStep?.creditNumbers} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"learnLanguage"}/></MyText>
                            <Input rootClassName={classes.formInput}
                                   value={LocalName.getName(data?.firstStep?.learnLanguage)} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black><IntlMessage id={"partnerOrg"}/></MyText>
                            <Input rootClassName={classes.formInput}
                                   value={LocalName.getName(data?.secondStep?.partnerOrg)} readOnly/>
                        </Flex>
                    </Flex>

                    {/* Описание и цель */}
                    <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"}>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('microqualPage.description') + " " + t('inRu')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.secondStep?.mainDirection?.nameRu} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('microqualPage.description') + " " + t('inKz')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.secondStep?.mainDirection?.nameKz} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('microqualPage.description') + " " + t('inEng')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.secondStep?.mainDirection?.nameEng} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('goalEPMQ') + " " + t('inRu')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.secondStep?.goal?.nameRu} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('goalEPMQ') + " " + t('inKz')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.secondStep?.goal?.nameKz} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('goalEPMQ') + " " + t('inEng')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.secondStep?.goal?.nameEng} readOnly/>
                        </Flex>
                    </Flex>
                </Flex>

                {/* Проф стандарты */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"profStandards"}/></MyText>
                    <Table
                        columns={profStandardsColumns}
                        dataSource={data?.firstStep?.professionalStandards}
                        pagination={false}
                        bordered
                        style={{width: "100%"}}
                    />
                </Flex>

                {/* Задачи */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.epTasks"}/></MyText>
                    <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"} gap={"small"}>
                        {data?.tasks?.map((item, index) => (
                            <Input value={LocalName.getName(item.name)} key={index} readOnly/>
                        ))}
                    </Flex>
                </Flex>

                {/* Компетенции */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.epCompetences"}/></MyText>
                    <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"} gap={"small"}>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('microqualPage.description') + " " + t('inRu')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.competences?.descOfCompetenceAndOutcomes?.nameRu} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('microqualPage.description') + " " + t('inKz')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.competences?.descOfCompetenceAndOutcomes?.nameKz} readOnly/>
                        </Flex>
                        <Flex vertical className={classes.formInput} style={{width: '100%', padding: 0}} gap={"small"}>
                            <MyText black>{t('microqualPage.description') + " " + t('inEng')}</MyText>
                            <Input.TextArea style={{height: 120}} rootClassName={classes.formInput}
                                            value={data?.competences?.descOfCompetenceAndOutcomes?.nameEng} readOnly/>
                        </Flex>
                        <MyText black><IntlMessage id={"microQ.competences"}/></MyText>
                        <Flex vertical align={"flex-start"} justify={"flex-start"} gap={"small"}
                              style={{width: '100%'}}>
                            {data?.competences?.list?.map((item, index) => (
                                <Input
                                    value={t('listOfCompetenciesShort') + item.name.number + " " + LocalName.getName(item.name)}
                                    key={index} readOnly/>
                            ))}
                        </Flex>
                    </Flex>
                </Flex>

                {/* Результаты обучения */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.resultsEd"}/></MyText>
                    <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"} gap={"small"}>
                        {data?.results?.map((item, index) => (
                            <Input
                                value={t('listOfOutcomesShort') + item.name.number + " " + LocalName.getName(item.name)}
                                key={index} readOnly/>
                        ))}
                    </Flex>
                </Flex>

                {/* Матрица */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.matrix"}/></MyText>
                    <Table
                        columns={matrixColumns}
                        dataSource={matrixDataSource}
                        pagination={false}
                        bordered
                        style={{width: "100%"}}
                    />
                </Flex>

                {/* Дисциплины */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.disciplines"}/></MyText>
                    <Table
                        columns={disciplinesColumns}
                        dataSource={data?.disciplines}
                        pagination={false}
                        bordered
                        style={{width: "100%"}}
                    />
                </Flex>

                {/* Авторы */}
                <Flex style={{width: "100%"}} vertical gap={"small"} align={"center"}>
                    <MyText strong uppercase size={"bigger"}><IntlMessage id={"micro.authorsAndDevs"}/></MyText>
                    <Table
                        columns={authorsColumns}
                        dataSource={data?.authors}
                        pagination={false}
                        bordered
                        style={{width: "100%"}}
                    />
                </Flex>

                {/* Модалка предпросмотра документа */}
                <DocumentPreviewModal
                    open={openViewDocument}
                    onClose={() => setOpenViewDocument(false)}
                    documentPdf={documentPdf}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                    isLoading={isLoadingDocument}
                    onDownload={downloadDocument}
                />

                {/* Модалка комментария */}
                <Form form={commentForm}>
                    <Modal
                        width={800}
                        maskClosable={true}
                        title={<IntlMessage id={'applicationsPage.comment'}/>}
                        open={openViewComment}
                        okText={<IntlMessage id={'save'}/>}
                        cancelText={<IntlMessage id={'close'}/>}
                        style={{top: 20}}
                        onOk={() => setOpenViewComment(false)}
                        onCancel={() => setOpenViewComment(false)}
                        footer={[
                            <Button key="submit" type="primary" onClick={commentMicroqualification}>
                                <IntlMessage id={'save'}/>
                            </Button>
                        ]}
                    >
                        {openViewComment && (
                            <Form.Item
                                labelCol={{span: 24}}
                                name="comment"
                                className={classes.formInputBlock}
                            >
                                <Input.TextArea
                                    style={{height: 120}}
                                    className={classes.formInput}
                                />
                            </Form.Item>
                        )}
                    </Modal>
                </Form>
            </Flex>
        </Flex>
    );
};

export default MicroQualificationView;
